import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    // Load cart from localStorage on mount
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (error) {
        console.error('Error loading cart:', error);
      }
    }
  }, []);

  useEffect(() => {
    // Save cart to localStorage whenever it changes
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, quantity = 1) => {
    setCartItems(prevItems => {
      const existingItem = prevItems?.find(item => item?.id === product?.id);
      
      if (existingItem) {
        // Update quantity if item already exists
        return prevItems?.map(item =>
          item?.id === product?.id
            ? { ...item, quantity: Math.min(item?.quantity + quantity, product?.maxQuantity || 10) }
            : item
        );
      } else {
        // Add new item to cart
        return [...prevItems, {
          id: product?.id,
          name: product?.name,
          price: product?.price,
          quantity: quantity,
          image: product?.image,
          imageAlt: product?.imageAlt || product?.name,
          maxQuantity: product?.maxQuantity || 10
        }];
      }
    });
  };

  const removeFromCart = (productId) => {
    setCartItems(prevItems => prevItems?.filter(item => item?.id !== productId));
  };

  const updateQuantity = (productId, newQuantity) => {
    setCartItems(prevItems =>
      prevItems?.map(item =>
        item?.id === productId
          ? { ...item, quantity: Math.max(1, Math.min(newQuantity, item?.maxQuantity)) }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems?.reduce((total, item) => total + (item?.price * item?.quantity), 0);
  };

  const getCartCount = () => {
    return cartItems?.reduce((count, item) => count + item?.quantity, 0);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;